import dayjs from 'dayjs';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../../dto/data-model.js';
import {
  cloneDataOrNull, cloneMap, isSameData, isSameMap,
  mapToData,
} from '../../dto/data-model-utils.js';
import { SeatingOptions } from '../seating-options.js';
import {
  IEventOpenAccessProtocol, OpenAccessProtocol, OpenAccessProtocolType,
} from './open-access-protocol.js';
import { SeatedOpenAccessProtocolModifier } from './seated-open-access-protocol-modifier.js';
import { JSONUtils } from '../../../utility/json-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { Cardinal } from '../../core/cardinal.js';
import { MigrationSafe } from '../../core/migration-safe.js';
import { BooleanOrListOfString } from '../../core/bool-or-list.js';
import { PassEventValidityRule } from './pass-event-validity-rule.js';

/**
 * @deprecated Use StagedSeatedOpenAccessProtocol instead
 */
export class SeatedOpenAccessProtocol extends OpenAccessProtocol implements IEventOpenAccessProtocol {
  static readonly TYPE_ID: string = 'df4fc95ef7124dbc8ef15935a24527c8';

  static readonly SCHEMA_VERSION: number = 5;

  public readonly type: OpenAccessProtocolType = OpenAccessProtocolType.PerSeat;

  private constructor(
      public readonly eventValidityRule: PassEventValidityRule,

      // Keyed by event ID. List of strings represents compatible pass variations.
      public readonly eventConfiguration: Map<string, BooleanOrListOfString>,
      public readonly maxEventCount: MigrationSafe<Cardinal>,
      public readonly startDate: dayjs.Dayjs,
      public readonly expirationDate: dayjs.Dayjs,

      public readonly roomId: string,
      public readonly seatingOptions: SeatingOptions | null,
      // Keyed by event ID.
      public readonly modifiers: Map<string, SeatedOpenAccessProtocolModifier>,
  ) {
    super();
  }

  private static create(args: {
    eventValidityRule: PassEventValidityRule,
    eventConfiguration: Map<string, BooleanOrListOfString>,
    maxEventCount: MigrationSafe<Cardinal>,
    startDate: dayjs.Dayjs,
    expirationDate: dayjs.Dayjs,
    roomId: string,
    seatingOptions: SeatingOptions | null,
    modifiers: Map<string, SeatedOpenAccessProtocolModifier>,
  }): SeatedOpenAccessProtocol {
    return new SeatedOpenAccessProtocol(
        args.eventValidityRule,
        args.eventConfiguration,
        args.maxEventCount,
        args.startDate,
        args.expirationDate,
        args.roomId,
        args.seatingOptions,
        args.modifiers,
    );
  }

  static forTemplate(args: {
    eventValidityRule: PassEventValidityRule,
    eventConfiguration: Map<string, BooleanOrListOfString>,
    startDate: dayjs.Dayjs,
    expirationDate: dayjs.Dayjs,
    roomId: string,
  }): SeatedOpenAccessProtocol {
    return this.create({
      ...args,
      maxEventCount: MigrationSafe.some(Cardinal.finite(0)),
      seatingOptions: null,
      modifiers: new Map<string, SeatedOpenAccessProtocolModifier>(),
    });
  }

  static forInstance(args: {
    eventValidityRule: PassEventValidityRule,
    eventConfiguration: Map<string, BooleanOrListOfString>,
    maxEventCount: Cardinal,
    startDate: dayjs.Dayjs,
    expirationDate: dayjs.Dayjs,
    roomId: string,
    seatingOptions: SeatingOptions,
  }): SeatedOpenAccessProtocol {
    return this.create({
      ...args,
      maxEventCount: MigrationSafe.some(args.maxEventCount.clone()),
      modifiers: new Map<string, SeatedOpenAccessProtocolModifier>(),
    });
  }

  get $typeId(): string {
    return SeatedOpenAccessProtocol.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(SeatedOpenAccessProtocol.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): SeatedOpenAccessProtocol {
    return new SeatedOpenAccessProtocol(
        toEnumFromString(MapUtils.getString(map, 'eventValidityRule'), PassEventValidityRule),
        MapUtils.getMap<string, BooleanOrListOfString>(
            map,
            'eventConfiguration',
            (key, value) => ({ key: key, value: BooleanOrListOfString.decode(value) }),
        ),
        MigrationSafe.decode(
            MapUtils.getRaw(map, 'maxEventCount'),
            (data) => Cardinal.decode(JSONUtils.decodeNumber(data)),
        ),
        context.decodeDate(MapUtils.getNumber(map, 'startDate')),
        context.decodeDate(MapUtils.getNumber(map, 'expirationDate')),
        MapUtils.getString(map, 'roomId'),
        context.fromDataOrNull<SeatingOptions>(
            MapUtils.getRecordOrNull(map, 'seatingOptions'),
        ),
        MapUtils.getMap<string, SeatedOpenAccessProtocolModifier>(
            map,
            'modifiers',
            (key, value) => ({
              key: key,
              value: context.fromData<SeatedOpenAccessProtocolModifier>(
                  JSONUtils.decodeRecord(value),
              ),
            }),
        ),
    );
  }

  toMap(context: SerializationContext):SerializedData {
    return {
      'type': fromEnumAsString(this.type),
      'eventValidityRule': fromEnumAsString(this.eventValidityRule),
      'eventConfiguration': mapToData(
          this.eventConfiguration,
          (key, value) => ({ key: key, value: value.encode() }),
      ),
      'maxEventCount': this.maxEventCount.encode((value) => value.encode()),
      'startDate': context.encodeDate(this.startDate),
      'expirationDate': context.encodeDate(this.expirationDate),
      'roomId': this.roomId,
      'seatingOptions': context.toDataOrNull(this.seatingOptions),
      'modifiers': mapToData(
          this.modifiers,
          (key, value) => ({
            key: key,
            value: context.toData(value),
          }),
      ),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  applyTemplateChanges(other: this): this {
    return this.copyWith({
      eventConfiguration: cloneMap(other.eventConfiguration, (v) => v.clone()),
      maxEventCount: this.maxEventCount.clone((value) => value.clone()),
      startDate: this.startDate,
      expirationDate: this.expirationDate,
    });
  }

  copyWith({
    eventValidityRule = this.eventValidityRule,
    eventConfiguration = cloneMap(this.eventConfiguration, (v) => v.clone()),
    maxEventCount = this.maxEventCount.clone((value) => value.clone()),
    startDate = this.startDate,
    expirationDate = this.expirationDate,
    roomId = this.roomId,
    seatingOptions = cloneDataOrNull(this.seatingOptions),
    modifiers = cloneMap(this.modifiers, (v) => v.clone()),
  }: {
    eventValidityRule?: PassEventValidityRule,
    eventConfiguration?: Map<string, BooleanOrListOfString>,
    maxEventCount?: MigrationSafe<Cardinal>,
    startDate?: dayjs.Dayjs,
    expirationDate?: dayjs.Dayjs,
    roomId?: string,
    seatingOptions?: SeatingOptions | null,
    modifiers?: Map<string, SeatedOpenAccessProtocolModifier>,
  } = {}): this {
    return SeatedOpenAccessProtocol.create({
      eventValidityRule,
      eventConfiguration,
      maxEventCount,
      startDate,
      expirationDate,
      roomId,
      seatingOptions,
      modifiers,
    }) as this;
  }

  isSame(other: SeatedOpenAccessProtocol): boolean {
    if(other === this) {
      return true;
    }

    return isSameMap(other.modifiers, this.modifiers, isSameData) &&
        other.maxEventCount.isSame(this.maxEventCount, (other, self) => other.isSame(self)) &&
        other.roomId == this.roomId &&
        isSameData(other.seatingOptions, this.seatingOptions) &&
        other.eventValidityRule === this.eventValidityRule &&
        isSameMap(other.eventConfiguration, this.eventConfiguration, isSameData) &&
        other.startDate.isSame(this.startDate) &&
        other.expirationDate.isSame(this.expirationDate);
  }

  cloneEventConfiguration(): Map<string, BooleanOrListOfString> {
    return cloneMap(this.eventConfiguration, (v) => v.clone());
  }

  isEventAssignedToPass({ eventId, passVariationId }: {
    eventId: string,
    passVariationId: string
  }): boolean {
    const entry = this.eventConfiguration.get(eventId);
    const isInEventConfiguration = entry != null && (
      (entry.isList && entry.listValue.includes(passVariationId)) ||
      (entry.isBoolean && entry.booleanValue)
    );

    return this.eventValidityRule === PassEventValidityRule.IncludedEvents ? isInEventConfiguration : !isInEventConfiguration;
  }

  isEventAssignedToTemplate({ eventId }: { eventId: string }): boolean {
    const entry = this.eventConfiguration.get(eventId);
    if(this.eventValidityRule === PassEventValidityRule.IncludedEvents) {
      return entry != null && (
        (entry.isList && entry.listValue.length > 0) ||
        (entry.isBoolean && entry.booleanValue)
      );
    }

    return entry == null || entry.isList || (entry.isBoolean && !entry.booleanValue);
  }

  getAssignedEventIdsForPass({ passVariationId }: { passVariationId: string }): string[] {
    const assignedEventIds: string[] = [];
    for(const eventId of this.eventConfiguration.keys()) {
      if(this.isEventAssignedToPass({ eventId, passVariationId })) {
        assignedEventIds.push(eventId);
      }
    }

    return assignedEventIds;
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof SeatedOpenAccessProtocol;
  }

  static assertType(dm: DataModel): dm is SeatedOpenAccessProtocol {
    return dm instanceof SeatedOpenAccessProtocol;
  }

  static convertToLegacyEventList(protocol: SeatedOpenAccessProtocol): string[] | null {
    return IEventOpenAccessProtocol.convertToLegacyEventList(protocol.eventConfiguration);
  }
}

export class SeatedOpenAccessProtocolBuilder extends DataModelBuilder<SeatedOpenAccessProtocol> {
  build(context: SerializationContext, map: SerializedData): SeatedOpenAccessProtocol {
    return SeatedOpenAccessProtocol.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(SeatedOpenAccessProtocol.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(SeatedOpenAccessProtocol.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // Version 2:
    // - add 'modifiers' field of type `Map<string, SeatedOpenAccessProtocolModifier>`.
    if(version < 2) {
      map['modifiers'] ??= mapToData(new Map<string, SeatedOpenAccessProtocolModifier>(), (key, value) => ({
        key: key,
        value: context.toData(value),
      }));

      version = 2;
    }

    // Version 3:
    // - add `maxEventCount` field of type `MigrationSafe<Cardinal>`.
    if(version < 3) {
      map['maxEventCount'] ??= MigrationSafe.UNDEFINED_STATUS;

      version = 3;
    }

    // Version 4:
    // - changed `event` from `string[]` to `Map<string, BooleanOrListOfString>.
    if(version < 4) {
      const events = IEventOpenAccessProtocol.convertFromLegacyEventList(
          MapUtils.getList<string>(map, 'events', (item) => JSONUtils.decodeString(item))
      );

      map['events'] = mapToData(
          events,
          (key, value) => ({ key: key, value: value.encode() }),
      );

      version = 4;
    }

    // Version 5:
    // - add 'eventValidityRule' field of type `PassEventValidityRule`.
    // - rename 'events' to 'eventConfiguration'.
    // - add 'startDate' and 'expirationDate' fields of type `dayjs.Dayjs`.
    if(version < 5) {
      const includedEvents = MapUtils.getMap<string, BooleanOrListOfString>(
          map,
          'events',
          (key, value) => ({ key: key, value: BooleanOrListOfString.decode(value) }),
      );
      const now = dayjs();

      map['eventValidityRule'] ??= fromEnumAsString(PassEventValidityRule.IncludedEvents);
      map['eventConfiguration'] ??= includedEvents;
      map['startDate'] ??= context.encodeDate(now);
      map['expirationDate'] ??= context.encodeDate(now.add(1000, 'year'));

      version = 5;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
